import { OrderedListDragDropData } from './../models/ordered-list-drag-drop-data';
import { OrderedListDragDropPopupComponent } from './../shared/components/ordered-list-drag-drop-popup/ordered-list-drag-drop-popup.component';
import { Subscription } from 'rxjs';
import { ControlsBaseComponent } from './../shared/components/controls-base.component';
import {
  ControlContainer,
  FormControl,
  FormGroupDirective,
  FormArray,
} from '@angular/forms';
import { CommonUtilsService } from './../shared/common-utils.service';
import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
  Input,
  ViewChild,
  Renderer2,
} from '@angular/core';
import { Config } from '../models/ui-config';
import { JsonDynamicComponentService } from '../json-dynamic-component.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { size, isEqual } from 'lodash';
import { CompareFuncService } from '../shared/services/compare-func.service';
import { DataViewComponent } from '../shared/components/data-view/data-view.component';
import { DataViewParameters } from '../models/data-view-parameters';
import { DEFAULT_DIALOG_CONFIG } from '../constants/lib-constants';

@Component({
  selector: 'ra-dropdown-control',
  templateUrl: './dropdown-control.component.html',
  styleUrls: ['./dropdown-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class DropdownControlComponent
  extends ControlsBaseComponent
  implements OnInit, OnDestroy
{
  showDragDropControl = false;
  filterCtrl = new FormControl('');
  @Input() formArr: FormArray;
  @ViewChild('matSelect') matSelect: MatSelect;
  searchTerm = '';
  private subscriptions: Subscription[] = [];
  id = CommonUtilsService.createGUID();
  showSpinner: boolean = false;
  compareValues: any;
  selectedValues = [];
  constructor(
    protected jsonDynamicComponentService: JsonDynamicComponentService,
    protected commonUtils: CommonUtilsService,
    private dialog: MatDialog,
    private renderer: Renderer2,
    private compareFuncService: CompareFuncService,
  ) {
    super(jsonDynamicComponentService, commonUtils);
  }

  ngOnInit() {
    this.compareValues = this.compareFuncService.compareValues;
    this.jsonDynamicComponentService.addDropdownControl(this.id, this);
    this.jsonDynamicComponentService.addDropdownControlInitialValues(this);
    if (this.currentConfig.order) {
      this.compareValues = () => 0;
    }
    this.currentConfig.minNumOfOptionsToShow =
      this.currentConfig.minNumOfOptionsToShow || Infinity;
    this.registerFilterValueChangeSubscription();
    if (
      this.parentFormGroup.get(this.currentConfig.property).value ||
      this.currentConfig.selectAllOnInit
    ) {
      if (!this.currentConfig.values) {
        // In read-only mode the control is disabled and the selected value is
        // rendered directly from the form value (see getSelectedValueTitle and
        // the hidden mat-options), so fetching the option list is redundant.
        // Dropdowns keyed by `dropdownID` still need the fetched options to map
        // ids to their display labels, so keep fetching for those.
        if (!this.readOnly || this.currentConfig.dropdownID) {
          this.getDropdownValues(this.currentConfig, true);
        }
      } else {
        this.selectAllValues(this.currentConfig);
      }
    }
  }

  private getSearchText(option: any): string {
    if (option == null) {
      return '';
    }
    const toLowerString = (val: any) => String(val ?? '').toLowerCase();
    const cfg = this.currentConfig;
    if (
      typeof option === 'string' ||
      typeof option === 'number' ||
      typeof option === 'boolean'
    ) {
      return toLowerString(option);
    }
    if (cfg?.dropdownLabel && option?.[cfg.dropdownLabel] != null) {
      return toLowerString(option[cfg.dropdownLabel]);
    }
    if (
      Array.isArray(cfg?.optionDisplayFields) &&
      cfg.optionDisplayFields.length > 0
    ) {
      const joined = cfg.optionDisplayFields
        .map((field) => option?.[field])
        .filter((v) => v != null)
        .join(' ');
      return toLowerString(joined);
    }
    try {
      return JSON.stringify(option).toLowerCase();
    } catch {
      return '';
    }
  }

  registerFilterValueChangeSubscription() {
    this.subscriptions[this.subscriptions.length] = this.filterCtrl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((value) => {
        this.currentConfig.loading = true;
        const filterValue = (value ?? '').toString().toLowerCase().trim();
        setTimeout(() => {
          let filteredValues: any[];
          if (!this.currentConfig?.isGrouped) {
            const allValues = this.currentConfig.values || [];
            filteredValues = allValues.filter((option: any) =>
              this.getSearchText(option).includes(filterValue),
            );
          } else {
            const groups = this.currentConfig.values || [];
            filteredValues = groups
              .map((group) => ({
                label: group.label,
                options: (group.options || []).filter((option) =>
                  this.getSearchText(option).includes(filterValue),
                ),
              }))
              .filter((group) => group.options.length > 0);
          }
          this.currentConfig.allOptions = filteredValues;
          const minNumOfOptionsToShow = Math.min(
            this.currentConfig.minNumOfOptionsToShow,
            filteredValues.length,
          );
          this.currentConfig.optionsToShow = filteredValues.slice(
            0,
            minNumOfOptionsToShow,
          );
          this.currentConfig.loading = false;
        }, 500);
      });
  }

  getDropdownValues(config: Config, isInitCall = false) {
    this.commonUtils
      .getDropdownValues(config, this.form?.value || {}, this.matSelect)
      .then((_x) => {
        if (
          isInitCall &&
          config.isMultiSelect &&
          config.selectAllOnInit &&
          size(this.parentFormGroup.get(this.currentConfig.property).value) ===
            0
        ) {
          this.selectAllValues(config);
        }
      });
  }

  selectAllValues(config: Config) {
    if (
      !config.isMultiSelect ||
      size(this.parentFormGroup.get(config.property).value)
    ) {
      return;
    }
    this.parentFormGroup
      .get(config.property)
      .setValue(
        config.dropdownID
          ? config.values.map((x) => x[config.dropdownID])
          : config.values,
      );
  }

  compareWithFn(x, y) {
    return isEqual(x, y);
  }

  /**
   * Whether an option should render disabled. Resolves the option to the same
   * value the template binds (honoring ``dropdownID`` / ``optionDisplayFields``)
   * and checks membership in ``currentConfig.disabledValues``. Returns false
   * when no disabled set is configured, so existing dropdowns are unaffected.
   */
  isOptionDisabled(option: any): boolean {
    const disabled = this.currentConfig?.disabledValues;
    if (!disabled?.length) {
      return false;
    }
    const cfg = this.currentConfig;
    const value = cfg.dropdownID
      ? option?.[cfg.dropdownID]
      : !cfg.optionDisplayFields || cfg.optionDisplayFields.length === 0
        ? option
        : option?.[cfg.optionDisplayFields[0]];
    return disabled.some((d) => isEqual(d, value));
  }

  updateAndSelectAll(config: Config) {
    this.showSpinner = true;
    this.commonUtils
      .getDropdownValues(config, this.form?.value || {}, this.matSelect)
      .then((_x) => {
        if (config.isMultiSelect && config.selectAllOnInit) {
          this.parentFormGroup
            .get(config.property)
            .setValue(
              config.dropdownID
                ? config.values.map((x) => x[config.dropdownID])
                : config.values,
              { emitEvent: false },
            );
        }
        this.showSpinner = false;
      });
  }

  validateSelectedValues(
    config: Config,
    configFilter: Function,
    hasModelChanged: boolean,
  ): Promise<boolean> {
    if (!configFilter(config)) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      this.showSpinner = true;
      const control = this.parentFormGroup.get(config.property);
      let currentValues = control?.value;
      if (currentValues == null) {
        currentValues =
          this.jsonDynamicComponentService.dropdownControlsInitialValues[
            this.id
          ];
      }
      this.commonUtils
        .getDropdownValues(
          config,
          this.form?.value || {},
          this.matSelect,
          hasModelChanged,
        )
        .then((_x) => {
          if (config.isMultiSelect) {
            currentValues = currentValues.filter(
              (x) =>
                config.values.includes(x) ||
                Object.values(config?.mapValueNames || []).includes(x),
            );
          } else {
            currentValues =
              config.values.includes(currentValues) ||
              Object.values(config?.mapValueNames || []).includes(currentValues)
                ? currentValues
                : null;
          }
          this.parentFormGroup
            .get(config.property)
            .setValue(currentValues, { emitEvent: false });
          if (config.isMultiSelect && config.selectAllOnInit) {
            this.parentFormGroup
              .get(config.property)
              .setValue(
                config.dropdownID
                  ? config.values.map((x) => x[config.dropdownID])
                  : config.values,
                { emitEvent: false },
              );
          }
          this.showSpinner = false;
          resolve(true);
        })
        .catch((_error) => {
          this.showSpinner = false;
          resolve(false);
        });
    });
  }

  onDropdownOpened(config: Config) {
    this.selectedValues =
      this.parentFormGroup?.get(config.property)?.value || [];
    this.getDropdownValues(config);
    // adding scroll event listener to mat-select panel
    // const panel = this.matSelect.panel.nativeElement;
    // panel.addEventListener('scroll', this.loadMoreOnScroll.bind(this));
  }

  // function to load more options on scroll
  // loads 20 options at a time
  // loadMoreOnScroll() {
  //   const panel = document.getElementsByClassName('mat-select-panel')[0];
  //   const scrollHeight = panel.scrollHeight;
  //   const scrollTop = panel.scrollTop;
  //   const clientHeight = panel.clientHeight;
  //   const scrollBottom = scrollHeight - scrollTop - clientHeight;
  //   const threshold = 20;
  //   if (scrollBottom < threshold && !this.currentConfig.loading && this.currentConfig.optionsToShow.length < this.currentConfig.allOptions.length) {
  //     this.currentConfig.loading = true;
  //     setTimeout(() => {
  //       const currLength = this.currentConfig.optionsToShow.length;
  //       const nextMin = currLength + Math.min(20, this.currentConfig.allOptions.length - currLength);
  //       const newOptions = this.currentConfig.allOptions.slice(currLength, nextMin);
  //       this.currentConfig.optionsToShow = [...this.currentConfig.optionsToShow, ...newOptions];
  //       this.currentConfig.loading = false;

  //       const newOptionsHeight = panel.querySelector('.mat-option').clientHeight * newOptions.length;
  //       const newScrollTop = scrollTop + newOptionsHeight;
  //       const maxScrollTop = scrollHeight - clientHeight;
  //       panel.scrollTo(0, Math.min(newScrollTop, maxScrollTop));
  //     }, 2000);
  //   }
  // }

  toggleDragDropControl() {
    this.showDragDropControl = !this.showDragDropControl;
    const ctrl = this.parentFormGroup?.get(this.currentConfig.property);
    const initialOrder: any[] = Array.isArray(ctrl?.value)
      ? (ctrl.value as any[]).slice()
      : [];
    const dialogRef = this.dialog.open(OrderedListDragDropPopupComponent, {
      width: 'auto',
      data: {
        topDirectionText: this.currentConfig?.topDirectionText,
        bottomDirectionText: this.currentConfig?.bottomDirectionText,
        topDirectionColor: this.currentConfig?.topDirectionColor,
        bottomDirectionColor: this.currentConfig?.bottomDirectionColor,
        allValues: this.currentConfig?.values,
        list: ctrl?.value,
        id: this.currentConfig.dropdownID,
        label: this.currentConfig.dropdownLabel,
      } as OrderedListDragDropData,
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(() => {
        const current: any[] = Array.isArray(ctrl?.value)
          ? (ctrl.value as any[])
          : [];
        if (!isEqual(initialOrder, current)) {
          ctrl?.setValue([...current]);
        }
      }),
    );
  }

  onViewClick() {
    let downloadURL = this.currentConfig?.downloadURL;
    downloadURL = this.commonUtils.getInterpolatedValue(
      downloadURL,
      this.form.value,
    );
    let fileStatsURL = this.currentConfig?.fileStatsURL;
    fileStatsURL = this.commonUtils.getInterpolatedValue(
      fileStatsURL,
      this.form.value,
    );
    this.dialog.open(DataViewComponent, {
      ...DEFAULT_DIALOG_CONFIG,
      width: 'auto',
      minWidth: '650px',
      height: '85%',
      data: {
        downloadURL: downloadURL,
        fileStatsURL: fileStatsURL,
        fileName: this.currentConfig?.fileName,
        customerName:
          this.jsonDynamicComponentService?.externalParams?.customer,
      } as DataViewParameters,
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.jsonDynamicComponentService.removeDropdownControl(this.id);
    this.jsonDynamicComponentService.removeDropdownControlInitialValues(this);
  }
}
